import { Action, createAction, handleActions } from 'redux-actions'
import { call, put, takeLatest } from 'redux-saga/effects'
import { oc } from 'ts-optchain'

import * as Bridge from '@/api/BridgeApi'
import { getCSRF } from '@/api/CsrfApi'
import { fetchInviteFriends, fetchLineFriends } from '@/api/PromotionApi'
import { LEAVE_ALERT_TYPE } from '@/components/common/layer/Leave/LeaveLayer'
import { PROMOTION_ROUTES } from '@/domains/CommonDomain'
import { IFriendsListType, IFriendType, ILineInviteRequest } from '@/domains/InviteDomain'
import { IRootState } from '@/modules'
import { layerActions } from '@/modules/common/layer'
import { loadingActions } from '@/modules/common/loading'
import { createPostAction } from '@/utils/actionUtils'
import { IFinGetLineInfoResult } from '@/utils/bridgeUtils/features/finGetLineInfo'
import { scrollToTop } from '@/utils/domUtils'
import { push } from '@/utils/historyUtils'

export interface IInviteState {
  info: IFriendsListType
  code: number
  message: string
}

const initialState: IInviteState = {
  info: {
    friends: [],
    totCnt: '',
  },
  code: 0,
  message: '',
}

export const INVITE = 'Invite'
export const LINE_FRIENDS = `${INVITE}/line-friends`
export const INVITE_FRIENDS = `${INVITE}/invite-friends`
export const PROMOTION_ERROR = `${INVITE}/failure`

// actions
export const inviteActions = {}

// post
export const lineFriendsAsync = createPostAction(LINE_FRIENDS)
export const inviteFriendsAsync = createPostAction(INVITE_FRIENDS)

export const promotionActions = {
  setError: createAction<Error>(PROMOTION_ERROR),
}

function getLineInfo(): Promise<IFinGetLineInfoResult> {
  try {
    return Bridge.finGetLineInfo()
  } catch (e) {
    if (!e) {
      throw Object.assign({}, { code: 'API ERROR', message: '' })
    }

    e.code = 'API ERROR'

    throw e
  }
}

export function* getLineFriends() {
  try {
    yield put(loadingActions.start(INVITE))
    yield call(getCSRF)

    const { result }: IFinGetLineInfoResult = yield call(getLineInfo)
    const response: IFriendsListType = yield call(fetchLineFriends, result)

    yield put(lineFriendsAsync.success(response))
  } catch (e) {
    yield put(promotionActions.setError(e))
    yield put(layerActions.open(LEAVE_ALERT_TYPE.PROMOTION))

    yield call(scrollToTop, 0)
  } finally {
    yield put(loadingActions.finish(INVITE))
  }
}

export function* inviteSelectedFriends(friends: Action<IFriendType[]>) {
  try {
    yield put(loadingActions.start(INVITE))
    yield call(getCSRF)

    const list = friends.payload
    const ids = list.map(item => {
      return {
        lineId: item.userId,
        lineUserNm: item.displayName,
      }
    })

    const params: ILineInviteRequest = { invitee: ids }
    // 결과값을 이용해야하는 경우
    // const {totCnt}:IInviteType = yield call(fetchInviteFriends, params)
    // yield put(inviteFriendsAsync.success(totCnt))
    yield call(fetchInviteFriends, params)

    //친구 목록과 초대한 친구 수 갱신
    yield call(getCSRF)
    const { result }: IFinGetLineInfoResult = yield call(getLineInfo)
    const response: IFriendsListType = yield call(fetchLineFriends, result)
    yield put(lineFriendsAsync.success(response))

    yield call(push, `${PROMOTION_ROUTES.SENT}`)
  } catch (e) {
    yield put(promotionActions.setError(e))
    yield put(layerActions.open(LEAVE_ALERT_TYPE.PROMOTION))
  } finally {
    yield put(loadingActions.finish(INVITE))
  }
}

export const reducer = {
  [lineFriendsAsync.SUCCESS]: (state: IInviteState, action: Action<any>) => ({
    ...state,
    info: action.payload,
  }),
  [PROMOTION_ERROR]: (state: IInviteState, action: Action<any>) => {
    const { code, message } = action.payload

    return {
      ...state,
      code: code,
      message: message,
    }
  },
}

export const inviteSelector = {
  info: (state: IRootState): IFriendsListType => ({
    friends: oc(state[INVITE]).info.friends(initialState.info.friends),
    totCnt: oc(state[INVITE]).info.totCnt(initialState.info.totCnt),
  }),
}

export const inviteReducer = handleActions(reducer, initialState)
export const inviteSaga = [
  takeLatest(lineFriendsAsync.POST, getLineFriends),
  takeLatest(inviteFriendsAsync.POST, inviteSelectedFriends),
]
